import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CountryCode } from '@capsico/contracts';
import { errorMessage } from '../api/client';
import { fetchWallet } from '../api/payments';
import { useCart } from './useCart';
import { useMyOrders } from './useMyOrders';
import { useRefundChoice } from './useRefunds';
import { useCashback } from './useCashback';

/**
 * Capsico Money (W11): store credit only — refunds, bonuses and goodwill, every credit with an
 * expiry, no top-ups. Each country has its own balance; the page opens on the country of the
 * current cart or the latest order. Served from cache and refreshed in the background.
 */
export function useWallet(messages: { saved: string }) {
  const cart = useCart();
  const orders = useMyOrders(true);
  const [chosen, setChosen] = useState<CountryCode | null>(null);

  const known = useMemo(() => {
    const set = new Set<CountryCode>();
    if (cart) set.add(cart.country);
    for (const row of orders.rows) set.add(row.country);
    return [...set];
  }, [cart, orders.rows]);

  const country: CountryCode = chosen ?? cart?.country ?? orders.rows[0]?.country ?? 'IN';

  const query = useQuery({
    queryKey: ['wallet', country],
    queryFn: () => fetchWallet(country),
    staleTime: 30_000,
  });
  const refunds = useRefundChoice({ country }, messages);
  // W18: cashback history (for the customer's cashback country) and a live win-back credit.
  const cashbackQuery = useCashback(true);
  const cashback =
    cashbackQuery.cashback && cashbackQuery.cashback.country === country
      ? cashbackQuery.cashback
      : null;
  const winback =
    query.data?.credits.find(
      (credit) => credit.source === 'winback' && credit.remainingMinor > 0,
    ) ?? null;

  return {
    country,
    /** Show a country switch only for customers who ordered in both. */
    countries: known.length > 1 ? (['IN', 'NP'] as CountryCode[]) : [],
    setCountry: setChosen,
    loading: query.isLoading,
    failed: query.isError && !query.data,
    error: query.error ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    wallet: query.data ?? null,
    choose: refunds.choose,
    /** Credited cashback lines, newest first (null until loaded or when growth is off). */
    cashbackRows: cashback ? cashback.rows.filter((row) => row.status === 'credited') : [],
    cashbackTotalMinor: cashback?.totalMinor ?? 0,
    cashbackPercent: cashback?.percent ?? null,
    winback,
    choosingId: refunds.choosingId,
  };
}
